import type { TicketBetItem } from './tickets';
import {
  applyLiveStatisticsPatch,
  formatStatPair,
  parseEventStatisticsRaw,
  parseLiveStatistics,
  resolveEventRowLiveInfo,
  type LiveEventStatisticsPatch,
  type LiveStatistics,
  type StatPair,
} from './live-statistics';
import type { LiveClockState } from './live-clock';

/** Football — legacy shows the inline stats widget for this sport only. */
export const MY_BETS_STATS_SPORT_ID = 1;

export type TicketBetLiveDisplay = {
  /** Bet was placed / flagged as live (`TicketBetItem.Live`). */
  isLiveBet: boolean;
  /** Show the Live badge (live and not finished). */
  showLiveBadge: boolean;
  /** Match has a final score or is no longer in play. */
  finished: boolean;
  /** Show score / clock row (live bet with score or finished). */
  showResult: boolean;
  homeScore?: string;
  awayScore?: string;
  /** Parenthetical score when the bet was placed, e.g. `(1:0)`. */
  betScoreLabel?: string;
  clock?: string;
  liveClockState?: LiveClockState;
  showStatsButton: boolean;
  homeRed: boolean;
  awayRed: boolean;
  statistics: LiveStatistics | null;
};

export type TicketEventStatisticsDto = {
  EventId: number;
  Period?: string | null;
  PeriodShort?: string | null;
  Time?: string | null;
  Score?: string | null;
  HomeScore?: number | string | null;
  AwayScore?: number | string | null;
  HomeRed?: number | null;
  AwayRed?: number | null;
  homescore?: number | string | null;
  awayscore?: number | string | null;
  homeyellow?: number | string | null;
  awayyellow?: number | string | null;
  homered?: number | string | null;
  awayred?: number | string | null;
  homecorners?: number | string | null;
  awaycorners?: number | string | null;
  homepenalties?: number | string | null;
  awaypenalties?: number | string | null;
};

export type TicketEventResultsDto = {
  EventId: number;
  /** Legacy returns a JSON string; some hosts may already deserialize it. */
  Statistics?: string | Record<string, unknown> | null;
};

function parseColonScore(score: string | null | undefined): StatPair | undefined {
  if (score == null) return undefined;
  const trimmed = String(score).trim();
  if (!trimmed || trimmed === ':') return undefined;
  const [home, away] = trimmed.split(':');
  if (home === undefined || away === undefined) return undefined;
  return { home: home.trim(), away: away.trim() };
}

function scoreFromParts(
  home: number | string | null | undefined,
  away: number | string | null | undefined,
): StatPair | undefined {
  if (home === null || home === undefined || away === null || away === undefined) {
    return undefined;
  }
  const homeText = String(home).trim();
  const awayText = String(away).trim();
  if (!homeText && !awayText) return undefined;
  return { home: homeText || '0', away: awayText || '0' };
}

/** BetScore shown in parentheses when the ticket list provides a non-empty value. */
export function formatBetScoreLabel(betScore: string | null | undefined): string | undefined {
  if (betScore == null) return undefined;
  const trimmed = String(betScore).trim();
  if (!trimmed || trimmed === ':') return undefined;
  return `(${trimmed})`;
}

/**
 * Legacy finished check: `Score !== null || !InPlay`.
 * For live UI we only treat as finished when the bet is live (or already has a score).
 */
export function isTicketBetFinished(bet: TicketBetItem): boolean {
  if (bet.Score != null && String(bet.Score).trim() !== '') return true;
  if (bet.Live === true && bet.InPlay === false) return true;
  return false;
}

export function shouldShowTicketBetStatsButton(
  bet: TicketBetItem,
  hasStatistics = false,
): boolean {
  // Legacy `BetsResultStats`: any bet with EventResults.Statistics gets the button.
  if (hasStatistics) return true;
  // Legacy initial render: football + in-play (and only while Live / Live tab).
  return Number(bet.SportId) === MY_BETS_STATS_SPORT_ID && bet.InPlay === true;
}

/**
 * Parse EventResults `Statistics` — legacy lowercase JSON string
 * (`score`, `yellowCards`, …). Also accepts an already-parsed object.
 */
export function parseTicketEventResultsStatistics(
  raw: string | Record<string, unknown> | null | undefined,
): LiveStatistics & { score?: StatPair } {
  const record =
    typeof raw === 'object' && raw !== null
      ? raw
      : parseEventStatisticsRaw(typeof raw === 'string' ? raw : null);
  if (!record) return {};

  const rawString =
    typeof raw === 'string'
      ? raw
      : typeof raw === 'object' && raw !== null
        ? JSON.stringify(raw)
        : null;

  const score =
    parseColonScore(
      typeof record.score === 'string'
        ? record.score
        : typeof record.Score === 'string'
          ? record.Score
          : undefined,
    ) ??
    (typeof record.score === 'object' || typeof record.Score === 'object'
      ? (() => {
          const pair = (record.score ?? record.Score) as Record<string, unknown>;
          const home = pair.Home ?? pair.home;
          const away = pair.Away ?? pair.away;
          if (home === undefined || away === undefined) return undefined;
          return { home: String(home), away: String(away) };
        })()
      : undefined);

  const fromLive = parseLiveStatistics(rawString);
  return {
    score,
    corners:
      fromLive?.corners ??
      parseColonScore(
        typeof record.corners === 'string'
          ? record.corners
          : typeof record.Corners === 'string'
            ? record.Corners
            : undefined,
      ),
    yellowCards:
      fromLive?.yellowCards ??
      parseColonScore(
        typeof record.yellowCards === 'string'
          ? record.yellowCards
          : typeof record.YellowCards === 'string'
            ? record.YellowCards
            : undefined,
      ),
    redCards:
      fromLive?.redCards ??
      parseColonScore(
        typeof record.redCards === 'string'
          ? record.redCards
          : typeof record.RedCards === 'string'
            ? record.RedCards
            : undefined,
      ),
    penalties:
      fromLive?.penalties ??
      parseColonScore(
        typeof record.penalties === 'string'
          ? record.penalties
          : typeof record.Penalties === 'string'
            ? record.Penalties
            : undefined,
      ),
  };
}

/** True when EventResults returned a usable Statistics payload for this event. */
export function eventResultsHasStatistics(
  statistics: string | Record<string, unknown> | null | undefined,
): boolean {
  if (statistics == null) return false;
  if (typeof statistics === 'string') return statistics.trim().length > 0;
  return Object.keys(statistics).length > 0;
}

/** Map HTTP `betting/statistics` row into live display enrichments. */
export function ticketEventStatisticsToPatch(
  event: TicketEventStatisticsDto,
): {
  patch: LiveEventStatisticsPatch;
  homeRed: boolean;
  awayRed: boolean;
  statistics: LiveStatistics;
  finished: boolean;
  clock?: string;
} {
  const scorePair =
    parseColonScore(event.Score ?? undefined) ??
    scoreFromParts(event.HomeScore ?? event.homescore, event.AwayScore ?? event.awayscore);

  const period = (event.Period ?? '').trim();
  const finished = period === 'Finished';

  let clock: string | undefined;
  if (finished) {
    clock = 'Finished';
  } else if (event.Time != null && String(event.Time).trim() !== '') {
    const short = event.PeriodShort?.trim();
    clock = `${short ? `${short} ` : ''}${event.Time}`;
  } else if (period) {
    clock = period;
  }

  const statistics: LiveStatistics = {
    corners: scoreFromParts(event.homecorners, event.awaycorners),
    yellowCards: scoreFromParts(event.homeyellow, event.awayyellow),
    redCards: scoreFromParts(event.homered, event.awayred),
    penalties: scoreFromParts(event.homepenalties, event.awaypenalties),
  };

  return {
    patch: {
      currentPart: finished ? 'Finished' : (event.Period ?? undefined) ?? undefined,
      currentPartShort: event.PeriodShort ?? undefined,
      currentTime: finished ? undefined : (event.Time ?? undefined) ?? undefined,
      score: scorePair
        ? { home: scorePair.home, away: scorePair.away }
        : undefined,
      corners: statistics.corners,
      yellowCards: statistics.yellowCards,
      redCards: statistics.redCards,
      penalties: statistics.penalties,
    },
    homeRed: (event.HomeRed ?? 0) > 0,
    awayRed: (event.AwayRed ?? 0) > 0,
    statistics,
    finished,
    clock,
  };
}

function mergeStatistics(
  base: LiveStatistics | null | undefined,
  patch: LiveEventStatisticsPatch | null | undefined,
): LiveStatistics | null {
  if (!base && !patch) return null;
  return {
    corners: patch?.corners ?? base?.corners,
    yellowCards: patch?.yellowCards ?? base?.yellowCards,
    redCards: patch?.redCards ?? base?.redCards,
    penalties: patch?.penalties ?? base?.penalties,
  };
}

/**
 * Resolve live / finished chrome for a My Bets leg.
 * Feed / HTTP patches override list `Score` / clock when present.
 */
export function resolveTicketBetLiveDisplay(
  bet: TicketBetItem,
  options?: {
    feedPatch?: LiveEventStatisticsPatch | null;
    httpEnrichment?: ReturnType<typeof ticketEventStatisticsToPatch> | null;
    resultsStatistics?: (LiveStatistics & { score?: StatPair }) | null;
    feedFinished?: boolean;
    translatePeriod?: (key: string) => string;
    anchoredAt?: number;
    /** Force stats button after EventResults returned statistics. */
    hasResultsStatistics?: boolean;
  },
): TicketBetLiveDisplay {
  const isLiveBet = bet.Live === true;
  const listFinished = isTicketBetFinished(bet);
  const http = options?.httpEnrichment ?? null;
  const results = options?.resultsStatistics ?? null;
  const feedPatch = options?.feedPatch ?? null;

  const finished =
    Boolean(options?.feedFinished) ||
    Boolean(http?.finished) ||
    listFinished;

  const listScore = parseColonScore(bet.Score);
  const resultsScore = results?.score;

  const snapshot = applyLiveStatisticsPatch(
    {
      HomeScore: listScore ? Number(listScore.home) : undefined,
      AwayScore: listScore ? Number(listScore.away) : undefined,
      Time: null,
      Period: finished ? 'Finished' : null,
      PeriodShort: null,
      Statistics: null,
    },
    feedPatch ?? http?.patch ?? undefined,
  );

  if (resultsScore && !feedPatch?.score && !http?.patch.score) {
    snapshot.HomeScore = Number(resultsScore.home);
    snapshot.AwayScore = Number(resultsScore.away);
  }

  const liveInfo = resolveEventRowLiveInfo(
    snapshot,
    options?.translatePeriod,
    options?.anchoredAt,
  );

  const hasScore =
    Boolean(feedPatch?.score) ||
    Boolean(http?.patch.score) ||
    Boolean(listScore) ||
    Boolean(resultsScore);

  const homeScore = hasScore
    ? (feedPatch?.score?.home !== undefined
        ? String(feedPatch.score.home)
        : http?.patch.score?.home !== undefined
          ? String(http.patch.score.home)
          : resultsScore?.home ?? listScore?.home ?? liveInfo.homeScore)
    : undefined;
  const awayScore = hasScore
    ? (feedPatch?.score?.away !== undefined
        ? String(feedPatch.score.away)
        : http?.patch.score?.away !== undefined
          ? String(http.patch.score.away)
          : resultsScore?.away ?? listScore?.away ?? liveInfo.awayScore)
    : undefined;

  let clock = liveInfo.clock;
  let liveClockState = liveInfo.liveClockState;
  if (finished) {
    clock = 'Finished';
    liveClockState = undefined;
  } else if (http?.clock && !feedPatch) {
    clock = http.clock;
    liveClockState = undefined;
  }

  const statistics = mergeStatistics(
    mergeStatistics(results ?? null, http?.statistics ?? null),
    feedPatch,
  );

  const showResult = isLiveBet && (hasScore || finished);
  const showLiveBadge = isLiveBet && !finished;
  const hasResultsStats =
    Boolean(options?.hasResultsStatistics) ||
    Boolean(statistics && hasStatValues(statistics));
  // Initial: live football in-play. After expand: any bet with EventResults.Statistics.
  const showStatsButton =
    hasResultsStats ||
    (isLiveBet && shouldShowTicketBetStatsButton(bet, false));

  const redFromFeed = feedPatch?.redCards
    ? {
        home: Number(feedPatch.redCards.home) > 0,
        away: Number(feedPatch.redCards.away) > 0,
      }
    : null;

  return {
    isLiveBet,
    showLiveBadge,
    finished,
    showResult,
    homeScore,
    awayScore,
    betScoreLabel: isLiveBet ? formatBetScoreLabel(bet.BetScore) : undefined,
    clock: isLiveBet ? clock : undefined,
    liveClockState: isLiveBet && !finished ? liveClockState : undefined,
    showStatsButton,
    homeRed: redFromFeed?.home ?? http?.homeRed ?? false,
    awayRed: redFromFeed?.away ?? http?.awayRed ?? false,
    statistics,
  };
}

function hasStatValues(stats: LiveStatistics): boolean {
  return Boolean(
    stats.corners || stats.yellowCards || stats.redCards || stats.penalties,
  );
}

/** Collect unique MatchIds from ticket bets (including custom selections). */
export function collectTicketMatchIds(
  bets: readonly TicketBetItem[] | null | undefined,
): number[] {
  const ids = new Set<number>();
  for (const bet of bets ?? []) {
    if (typeof bet.MatchId === 'number' && Number.isFinite(bet.MatchId) && bet.MatchId > 0) {
      ids.add(bet.MatchId);
    }
    for (const selection of bet.Selections ?? []) {
      if (
        typeof selection.MatchId === 'number' &&
        Number.isFinite(selection.MatchId) &&
        selection.MatchId > 0
      ) {
        ids.add(selection.MatchId);
      }
    }
  }
  return [...ids];
}

export function formatTicketStatPair(value: StatPair | undefined): StatPair {
  return formatStatPair(value);
}
